<script setup>
import { nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import AppIcon from './AppIcon.vue'
import { hasOpenDialogs, isTopDialog, registerDialog, unregisterDialog } from '../composables/dialog-stack'

const props = defineProps({
  open: Boolean,
  title: { type: String, required: true },
  eyebrow: { type: String, default: '' },
  wide: Boolean,
})
const emit = defineEmits(['close'])
const titleId = useId()
const panelRef = ref(null)
const dialogKey = Symbol('dialog')

let previousActive = null

function handleKeydown(event) {
  if (!isTopDialog(dialogKey)) return
  if (event.key === 'Escape') {
    emit('close')
    return
  }
  if (event.key !== 'Tab' || !panelRef.value) return
  const focusable = [...panelRef.value.querySelectorAll(
    'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
  )].filter((element) => !element.hidden && element.getClientRects().length)
  if (!focusable.length) {
    event.preventDefault()
    return
  }
  const first = focusable[0]
  const last = focusable.at(-1)
  if (!panelRef.value.contains(document.activeElement)) {
    event.preventDefault()
    ;(event.shiftKey ? last : first).focus()
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

watch(() => props.open, async (open) => {
  if (open) {
    registerDialog(dialogKey)
    previousActive = document.activeElement
    document.body.classList.add('modal-open')
    window.addEventListener('keydown', handleKeydown)
    await nextTick()
    if (isTopDialog(dialogKey)) panelRef.value?.querySelector('[autofocus], input, button')?.focus()
  } else {
    unregisterDialog(dialogKey)
    document.body.classList.toggle('modal-open', hasOpenDialogs())
    window.removeEventListener('keydown', handleKeydown)
    if (previousActive?.isConnected) previousActive.focus?.()
  }
}, { immediate: true })

onBeforeUnmount(() => {
  unregisterDialog(dialogKey)
  document.body.classList.toggle('modal-open', hasOpenDialogs())
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="open" class="modal-backdrop" @mousedown.self="emit('close')">
        <section ref="panelRef" class="modal-panel" :class="{ 'modal-panel--wide': wide }" role="dialog" aria-modal="true" :aria-labelledby="titleId">
          <header class="modal-header">
            <div>
              <span v-if="eyebrow" class="eyebrow">{{ eyebrow }}</span>
              <h2 :id="titleId">{{ title }}</h2>
            </div>
            <button type="button" class="icon-button" aria-label="Закрыть" @click="emit('close')">
              <AppIcon name="close" />
            </button>
          </header>
          <div class="modal-body">
            <slot />
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>
